import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminSeverityText,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminSortableTableHeader,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  AdminListFilterDropdown,
  AdminMultiSelectFilterDropdown,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

const CATEGORY_OPTIONS = ['Abuse', 'Illegal', 'Safety', 'Spam', 'Compliance'];
const APPLIES_TO_OPTIONS = ['Message', 'Review', 'Post', 'Quote', 'Document', 'Profile'];
const SEVERITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];
const STATUS_OPTIONS = ['Active', 'Testing', 'Disabled'];
const TRIGGER_TYPE_OPTIONS = ['Keyword', 'Pattern', 'Behavioural'];
const FLAG_REASON_OPTIONS = ['Harassment', 'Scam', 'Abuse', 'Spam', 'Compliance', 'Copyright', 'Policy', 'Other'];
const SYSTEM_FLAG_OPTIONS = ['Yes', 'No'];

const buildDummyRules = () => {
  return [
    {
      id: 'RULE-ABUSE-001',
      ruleId: 'RULE-ABUSE-001',
      name: 'Abusive Language — Direct Insults',
      category: 'Abuse',
      severity: 'High',
      appliesTo: ['Message', 'Review', 'Post'],
      triggerType: 'Keyword',
      flagReason: 'Harassment',
      systemFlag: 'Yes',
      defaultEnforcement: 'Remove Content + Warning',
      status: 'Active',
      description: 'Detects direct insults and abusive language patterns',
      keywords: ['insult', 'abuse', 'harassment']
    },
    {
      id: 'RULE-ABUSE-004',
      ruleId: 'RULE-ABUSE-004',
      name: 'Threatening Language',
      category: 'Abuse',
      severity: 'High',
      appliesTo: ['Message', 'Post'],
      triggerType: 'Keyword',
      flagReason: 'Abuse',
      systemFlag: 'Yes',
      defaultEnforcement: 'Remove Content + Escalate',
      status: 'Active',
      description: 'Detects threatening language',
      keywords: ['threat', 'kill', 'harm', 'violence']
    },
    {
      id: 'RULE-ILLEGAL-002',
      ruleId: 'RULE-ILLEGAL-002',
      name: 'Illegal Service Offer',
      category: 'Illegal',
      severity: 'Critical',
      appliesTo: ['Post', 'Message'],
      triggerType: 'Pattern',
      flagReason: 'Compliance',
      systemFlag: 'Yes',
      defaultEnforcement: 'Escalate to Compliance',
      status: 'Active',
      description: 'Detects offers of illegal services',
      keywords: ['illegal', 'unlicensed']
    },
    {
      id: 'RULE-SPAM-007',
      ruleId: 'RULE-SPAM-007',
      name: 'Mass Messaging Behaviour',
      category: 'Spam',
      severity: 'Medium',
      appliesTo: ['Message'],
      triggerType: 'Behavioural',
      flagReason: 'Spam',
      systemFlag: 'Yes',
      defaultEnforcement: 'Messaging Restricted',
      status: 'Testing',
      description: 'Detects mass messaging behavior',
      keywords: []
    },
    {
      id: 'RULE-POL-010',
      ruleId: 'RULE-POL-010',
      name: 'Allowed Opinion Content',
      category: 'Compliance',
      severity: 'Low',
      appliesTo: ['Review', 'Post'],
      triggerType: 'Keyword',
      flagReason: 'Policy',
      systemFlag: 'No',
      defaultEnforcement: 'No Action',
      status: 'Active',
      description: 'Identifies allowed opinion content',
      keywords: []
    },
  ];
};

const ModerationRulebook = () => {
  const navigate = useNavigate();

  const [rules] = useState(buildDummyRules);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category: [],
    appliesTo: [],
    severity: [],
    triggerType: [],
    flagReason: [],
    systemFlag: null,
    status: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('ruleId');
  const [sortOrder, setSortOrder] = useState('asc');
  const [rowActionsOpen, setRowActionsOpen] = useState({});
  const rowActionsRefs = useRef({});

  // Refs for dropdowns
  const categoryRef = useRef(null);
  const appliesToRef = useRef(null);
  const severityRef = useRef(null);
  const triggerTypeRef = useRef(null);
  const flagReasonRef = useRef(null);
  const systemFlagRef = useRef(null);
  const statusRef = useRef(null);

  // Dropdown states
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [appliesToOpen, setAppliesToOpen] = useState(false);
  const [severityOpen, setSeverityOpen] = useState(false);
  const [triggerTypeOpen, setTriggerTypeOpen] = useState(false);
  const [flagReasonOpen, setFlagReasonOpen] = useState(false);
  const [systemFlagOpen, setSystemFlagOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  // Click outside handlers
  useClickOutside(categoryRef, () => setCategoryOpen(false));
  useClickOutside(appliesToRef, () => setAppliesToOpen(false));
  useClickOutside(severityRef, () => setSeverityOpen(false));
  useClickOutside(triggerTypeRef, () => setTriggerTypeOpen(false));
  useClickOutside(flagReasonRef, () => setFlagReasonOpen(false));
  useClickOutside(systemFlagRef, () => setSystemFlagOpen(false));
  useClickOutside(statusRef, () => setStatusOpen(false));

  const filteredRules = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return rules.filter((rule) => {
      // Search filter - only Rule ID and Rule Name
      const matchesSearch =
        !lowerSearch ||
        rule.name.toLowerCase().includes(lowerSearch) ||
        rule.ruleId.toLowerCase().includes(lowerSearch);

      // Category filter
      const matchesCategory =
        filters.category.length === 0 || filters.category.includes(rule.category);

      // Applies To filter
      const matchesAppliesTo =
        filters.appliesTo.length === 0 || 
        filters.appliesTo.some(type => rule.appliesTo.includes(type));

      // Severity filter (multi-select)
      const matchesSeverity =
        filters.severity.length === 0 || filters.severity.includes(rule.severity);

      // Trigger Type filter
      const matchesTriggerType =
        filters.triggerType.length === 0 || filters.triggerType.includes(rule.triggerType);

      // Flag Reason filter
      const matchesFlagReason =
        filters.flagReason.length === 0 || filters.flagReason.includes(rule.flagReason);

      // System Flag filter (single-select)
      const matchesSystemFlag =
        !filters.systemFlag || 
        (filters.systemFlag === 'Yes' && rule.systemFlag === 'Yes') ||
        (filters.systemFlag === 'No' && rule.systemFlag === 'No');

      // Status filter (multi-select)
      const matchesStatus =
        filters.status.length === 0 || filters.status.includes(rule.status);

      return matchesSearch && matchesCategory && matchesAppliesTo && matchesSeverity && 
             matchesTriggerType && matchesFlagReason && matchesSystemFlag && matchesStatus;
    });
  }, [rules, search, filters]);

  const sortedRules = useMemo(() => {
    const sorted = [...filteredRules].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'appliesTo') {
        aValue = a.appliesTo.join(', ');
        bValue = b.appliesTo.join(', ');
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      } else if (typeof aValue === 'boolean') {
        aValue = aValue ? 'yes' : 'no';
        bValue = bValue ? 'yes' : 'no';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return sorted;
  }, [filteredRules, sortField, sortOrder]);

  const totalCount = sortedRules.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, Math.max(1, Math.ceil(sortedRules.length / CONFIG_LIST_PAGE_SIZE))));
  }, [sortedRules.length]);

  const paginatedRules = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return sortedRules.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [sortedRules, effectivePage]);

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'category' || filterType === 'appliesTo' || filterType === 'severity' || 
        filterType === 'triggerType' || filterType === 'flagReason' || filterType === 'status') {
      setFilters(prev => ({
        ...prev,
        [filterType]: prev[filterType].includes(value)
          ? prev[filterType].filter(item => item !== value)
          : [...prev[filterType], value],
      }));
    } else if (filterType === 'systemFlag') {
      setFilters(prev => ({
        ...prev,
        [filterType]: prev[filterType] === value ? null : value,
      }));
    }
    // Reset to first page when filters change
    setCurrentPage(1);
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleRowActions = (ruleId) => {
    setRowActionsOpen((prev) => {
      if (prev[ruleId]) return {};
      return { [ruleId]: true };
    });
  };

  const handleRowClick = (rule) => {
    navigate(`/admin/moderation/rules/${rule.id}`, { state: { rule } });
  };

  const handleRowAction = (action, rule) => {
    setRowActionsOpen(prev => ({ ...prev, [rule.id]: false }));
    
    switch (action) {
      case 'view':
      case 'edit':
        navigate(`/admin/moderation/rules/${rule.id}`, { state: { rule } });
        break;
      case 'duplicate':
        navigate(`/admin/moderation/rules/new`, { state: { duplicateFrom: rule } });
        break;
      case 'disable':
        if (window.confirm(`Are you sure you want to ${rule.status === 'Active' ? 'disable' : 'enable'} this rule?`)) {
          // TODO: Implement disable/enable API call
          console.log(`${rule.status === 'Active' ? 'Disable' : 'Enable'} rule:`, rule.ruleId);
        }
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
          // TODO: Implement delete API call
          console.log('Delete rule:', rule.ruleId);
        }
        break;
      default:
        break;
    }
  };

  // Click outside handler for row actions
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(rowActionsRefs.current).forEach((ruleId) => {
        const ref = rowActionsRefs.current[ruleId];
        if (ref && !ref.contains(event.target)) {
          setRowActionsOpen(prev => ({ ...prev, [ruleId]: false }));
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <AdminPageTitle>Master Moderation Rulebook</AdminPageTitle>
            <AdminHeaderAction
              onClick={() => navigate('/admin/moderation/rules/new')}
            >
              + Add Rule
            </AdminHeaderAction>
          </div>

        {/* Search and Filters Row */}
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            {/* Search - Left */}
            <div className="w-full max-w-[450px]">
              <AdminSearchField
                placeholder="Search Rule ID, Rule Name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Filters - Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminMultiSelectFilterDropdown
                label="Category"
                emptyLabel="All Categories"
                selected={filters.category}
                options={CATEGORY_OPTIONS}
                onToggle={(value) => handleFilterChange('category', value)}
                menuRef={categoryRef}
                open={categoryOpen}
                setOpen={setCategoryOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Applies To"
                emptyLabel="All Applies To"
                selected={filters.appliesTo}
                options={APPLIES_TO_OPTIONS}
                onToggle={(value) => handleFilterChange('appliesTo', value)}
                menuRef={appliesToRef}
                open={appliesToOpen}
                setOpen={setAppliesToOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Severity"
                emptyLabel="All Severities"
                selected={filters.severity}
                options={SEVERITY_OPTIONS}
                onToggle={(value) => handleFilterChange('severity', value)}
                menuRef={severityRef}
                open={severityOpen}
                setOpen={setSeverityOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Trigger Type"
                emptyLabel="All Trigger Types"
                selected={filters.triggerType}
                options={TRIGGER_TYPE_OPTIONS}
                onToggle={(value) => handleFilterChange('triggerType', value)}
                menuRef={triggerTypeRef}
                open={triggerTypeOpen}
                setOpen={setTriggerTypeOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Flag Reason"
                emptyLabel="All Flag Reasons"
                selected={filters.flagReason}
                options={FLAG_REASON_OPTIONS}
                onToggle={(value) => handleFilterChange('flagReason', value)}
                menuRef={flagReasonRef}
                open={flagReasonOpen}
                setOpen={setFlagReasonOpen}
              />
              <AdminListFilterDropdown
                label="System Flag"
                value={filters.systemFlag}
                options={[
                  { value: null, label: 'All System Flags' },
                  ...SYSTEM_FLAG_OPTIONS.map((flag) => ({ value: flag, label: flag })),
                ]}
                onChange={(value) => {
                  setFilters((prev) => ({ ...prev, systemFlag: value }));
                  setCurrentPage(1);
                }}
                menuRef={systemFlagRef}
                open={systemFlagOpen}
                setOpen={setSystemFlagOpen}
              />
              <AdminMultiSelectFilterDropdown
                label="Status"
                emptyLabel="All Statuses"
                selected={filters.status}
                options={STATUS_OPTIONS}
                onToggle={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
            </div>
          </div>
        </div>

        {/* Rules Table */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {/* Header */}
          <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4" style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(120px, 1.2fr) minmax(80px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(60px, 0.6fr)' }}>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('ruleId')}>Rule ID</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('name')}>Name</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('category')}>Category</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('severity')}>Severity</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('appliesTo')}>Applies To</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('triggerType')}>Trigger Type</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('flagReason')}>Flag Reason</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('systemFlag')}>System Flag</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('defaultEnforcement')}>Default Enforcement</AdminSortableTableHeader>
            </div>
            <div className="flex items-center">
              <AdminSortableTableHeader onClick={() => handleSort('status')}>Status</AdminSortableTableHeader>
            </div>
            <div className="flex items-center justify-center">
              <AdminTableHeader>Actions</AdminTableHeader></div>
          </div>

          {/* Rows */}
          {paginatedRules.length === 0 ? (
            <div className="py-10 px-4 text-center">
              <AdminMainText className="text-mediumgray">
                No moderation rules found.
              </AdminMainText>
            </div>
          ) : (
            paginatedRules.map((rule) => (
              <div
                key={rule.id}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 cursor-pointer"
                style={{ gridTemplateColumns: 'minmax(120px, 1.2fr) minmax(200px, 2fr) minmax(100px, 1fr) minmax(100px, 1fr) minmax(150px, 1.5fr) minmax(120px, 1.2fr) minmax(120px, 1.2fr) minmax(80px, 1fr) minmax(150px, 1.5fr) minmax(100px, 1fr) minmax(60px, 0.6fr)' }}
                onClick={() => handleRowClick(rule)}
              >
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.ruleId}>
                    {rule.ruleId}
                  </span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.name}>
                    {rule.name}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-offblack">{rule.category}</span>
                </div>
                <div className="flex items-center">
                  <AdminSeverityText severity={rule.severity}>{rule.severity}</AdminSeverityText>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.appliesTo.join(', ')}>
                    {rule.appliesTo.join(', ')}
                  </span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.triggerType}>
                    {rule.triggerType}
                  </span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.flagReason}>
                    {rule.flagReason}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-offblack">{rule.systemFlag}</span>
                </div>
                <div className="flex items-center min-w-0">
                  <span className="text-offblack truncate" title={rule.defaultEnforcement}>
                    {rule.defaultEnforcement}
                  </span>
                </div>
                <div className="flex items-center">
                  <AdminStatusBadge status={rule.status} label={rule.status} />
                </div>
                <div className="flex items-center justify-center relative" onClick={(e) => e.stopPropagation()}>
                  <div className="relative inline-flex" ref={el => rowActionsRefs.current[rule.id] = el}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRowActions(rule.id);
                      }}
                      className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                      title="Row Actions"
                    >
                      <i className="fi fi-rs-menu-dots-vertical text-offblack flex items-center justify-center"></i>
                    </button>
                    {rowActionsOpen[rule.id] && (
                      <div className="absolute right-0 top-full mt-1 bg-white rounded-lg z-30 min-w-[200px] shadow-lg border border-lightgray">
                        <div className="py-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('view', rule);
                            }}
                            className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                          >
                            <AdminSupportingText>View / Edit Rule</AdminSupportingText>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('duplicate', rule);
                            }}
                            className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                          >
                            <AdminSupportingText>Duplicate Rule</AdminSupportingText>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('disable', rule);
                            }}
                            className="block px-3 py-1.5 hover:text-primaryblue w-full text-left"
                          >
                            <AdminSupportingText>
                              {rule.status === 'Active' ? 'Disable Rule' : 'Enable Rule'}
                            </AdminSupportingText>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowAction('delete', rule);
                            }}
                            className="block px-3 py-1.5 hover:text-alertred w-full text-left"
                          >
                            <AdminSupportingText>Delete Rule</AdminSupportingText>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default ModerationRulebook;

